import React, { useState } from "react";
import { X, ShoppingBag, Plus, Minus, Trash2 } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

export default function CartDrawer({
  isOpen,
  onClose,
  cartItems,
  onUpdateQty,
  onRemoveItem,
  onClearCart
}) {
  const [checkoutStep, setCheckoutStep] = useState("idle"); // idle, details, success
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [orderId, setOrderId] = useState("");

  const totalAmount = cartItems.reduce(
    (acc, item) => acc + item.price * item.quantity,
    0
  );

  const handleCheckoutSubmit = async (e) => {
    e.preventDefault();
    if (!customerName || !customerEmail) return;

    try {
      setCheckoutStep("submitting");
      const orderPayload = {
        items: cartItems.map((item) => ({
          productId: item.productId,
          name: item.name,
          price: item.price,
          size: item.size,
          color: item.color,
          quantity: item.quantity
        })),
        totalAmount: totalAmount,
        customerName,
        customerEmail
      };

      const response = await axios.post(`${API}/orders`, orderPayload);
      if (response.status === 200 || response.status === 201) {
        setOrderId(response.data._id || response.data.id || "WVL-" + Math.floor(1000 + Math.random() * 9000));
        setCheckoutStep("success");
        onClearCart();
      }
    } catch (err) {
      console.error("Checkout failed", err);
      setCheckoutStep("idle");
      alert("Error al tramitar tu pedido. Por favor inténtalo de nuevo.");
    }
  };

  // Pago real con Stripe: crea la sesión en el backend y redirige.
  const handleStripeCheckout = async () => {
    if (cartItems.length === 0) return;
    try {
      setCheckoutStep("submitting");
      const res = await axios.post(`${API}/checkout`, {
        items: cartItems.map((it) => ({
          productId: it.productId,
          size: it.size,
          color: it.color,
          quantity: it.quantity,
        })),
        origin: window.location.origin,
      });
      if (res.data && res.data.configured === false) {
        // Stripe aún no configurado → flujo de pedido simulado como respaldo
        setCheckoutStep("details");
        return;
      }
      if (res.data && res.data.url) {
        window.location.href = res.data.url;
        return;
      }
      setCheckoutStep("idle");
    } catch (err) {
      console.error("Stripe checkout error", err);
      setCheckoutStep("idle");
      alert("Error al iniciar el pago. Inténtalo de nuevo.");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden font-body">
      {/* Background Overlay */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
        onClick={onClose}
        data-testid="cart-overlay"
      />

      <div className="absolute inset-y-0 right-0 max-w-full flex">
        <div
          data-testid="cart-drawer-panel"
          className="w-screen max-w-md bg-white text-text_primary flex flex-col shadow-2xl rounded-none border-l border-border_subtle"
          style={{ backgroundColor: "#FFFFFF", color: "#0A0A0A" }}
        >
          {/* Header */}
          <div className="p-6 border-b border-border_subtle flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ShoppingBag className="w-5 h-5" />
              <h2 className="font-mono-accent text-sm uppercase tracking-widest text-text_primary">
                BOLSA DE COMPRA ({cartItems.length})
              </h2>
            </div>
            <button
              onClick={onClose}
              data-testid="cart-close-button"
              className="text-text_secondary hover:text-text_primary transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {checkoutStep === "success" ? (
              /* Success Step */
              <div className="h-full flex flex-col items-center justify-center text-center p-6 space-y-4" data-testid="checkout-success-view">
                <div className="w-16 h-16 bg-[#4F46E5]/10 flex items-center justify-center border border-[#4F46E5] text-[#4F46E5]">
                  <ShoppingBag className="w-8 h-8" />
                </div>
                <h3 className="font-heading italic text-3xl leading-tight">¡Pedido Recibido!</h3>
                <p className="text-sm text-text_secondary">
                  Gracias por tu confianza en Wovll, <strong className="text-text_primary">{customerName}</strong>. 
                  Hemos enviado un email de confirmación simulado a <strong className="text-text_primary">{customerEmail}</strong>.
                </p>
                <div className="bg-[#F4F4F2] p-4 w-full font-mono-accent text-xs uppercase tracking-wider">
                  N° PEDIDO: {orderId}
                </div>
                <button
                  onClick={() => {
                    setCheckoutStep("idle");
                    setCustomerName("");
                    setCustomerEmail("");
                    onClose();
                  }}
                  data-testid="checkout-success-continue-button"
                  className="w-full py-4 bg-[#0A0A0A] text-white hover:bg-[#FF3B30] transition-colors font-mono-accent uppercase tracking-widest text-xs"
                >
                  Seguir Comprando
                </button>
              </div>
            ) : checkoutStep === "details" ? (
              /* Customer Info details checkout */
              <form onSubmit={handleCheckoutSubmit} className="space-y-6" data-testid="checkout-form">
                <h3 className="font-heading italic text-2xl">Datos de Facturación</h3>
                <p className="text-sm text-text_secondary">
                  Ingresa tus datos para completar tu pedido de streetwear de alta gama Wovll.
                </p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-mono-accent uppercase tracking-widest mb-2">
                      Nombre Completo
                    </label>
                    <input
                      type="text"
                      required
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      data-testid="checkout-name-input"
                      placeholder="Ej. Carlos Mendoza"
                      className="w-full bg-[#F4F4F2] border border-border_subtle rounded-none px-4 py-3 text-sm focus:outline-none focus:border-[#4F46E5] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-mono-accent uppercase tracking-widest mb-2">
                      Correo Electrónico
                    </label>
                    <input
                      type="email"
                      required
                      value={customerEmail}
                      onChange={(e) => setCustomerEmail(e.target.value)}
                      data-testid="checkout-email-input"
                      placeholder="Ej. carlos@wovll.com"
                      className="w-full bg-[#F4F4F2] border border-border_subtle rounded-none px-4 py-3 text-sm focus:outline-none focus:border-[#4F46E5] transition-colors"
                    />
                  </div>
                </div>

                <div className="pt-6 border-t border-border_subtle space-y-4">
                  <div className="flex justify-between font-mono-accent text-xs uppercase tracking-widest">
                    <span>Total a Pagar:</span>
                    <span className="font-semibold text-sm">{totalAmount.toFixed(2)} €</span>
                  </div>
                  <button
                    type="submit"
                    data-testid="checkout-form-submit"
                    className="w-full py-4 bg-[#0A0A0A] text-white hover:bg-[#FF3B30] transition-colors font-mono-accent uppercase tracking-widest text-xs"
                  >
                    Confirmar Pedido Simulado
                  </button>
                  <button
                    type="button"
                    onClick={() => setCheckoutStep("idle")}
                    className="w-full py-3 border border-border_subtle text-text_secondary hover:text-text_primary transition-colors font-mono-accent uppercase tracking-widest text-[10px]"
                  >
                    Volver a la Bolsa
                  </button>
                </div>
              </form>
            ) : cartItems.length === 0 ? (
              /* Empty Cart state */
              <div className="h-full flex flex-col items-center justify-center text-center p-6 space-y-4">
                <ShoppingBag className="w-12 h-12 text-text_secondary/30" />
                <h3 className="font-heading italic text-2xl">Bolsa Vacía</h3>
                <p className="text-sm text-text_secondary max-w-[250px]">
                  No has agregado ninguna prenda a tu bolsa. ¡Visita nuestra tienda para inspirarte!
                </p>
                <button
                  onClick={onClose}
                  className="px-6 py-3 bg-[#0A0A0A] text-white hover:bg-[#4F46E5] transition-colors font-mono-accent uppercase tracking-widest text-xs"
                >
                  Explorar Tienda
                </button>
              </div>
            ) : (
              /* Cart Line Items */
              <div className="space-y-4">
                {cartItems.map((item, idx) => (
                  <div
                    key={`${item.productId}-${item.size}-${item.color}-${idx}`}
                    data-testid={`cart-item-${item.productId}`}
                    className="flex gap-4 pb-4 border-b border-border_subtle"
                  >
                    <img
                      src={item.imageUrl}
                      alt={item.name}
                      className="w-20 h-24 object-cover border border-border_subtle"
                    />
                    <div className="flex-1 flex flex-col justify-between">
                      <div>
                        <h4 className="font-heading text-lg font-medium tracking-tight">
                          {item.name}
                        </h4>
                        <div className="flex gap-4 mt-1 font-mono-accent text-[10px] text-text_secondary uppercase tracking-widest">
                          <span>Talla: {item.size}</span>
                          <span>Color: {item.color}</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        {/* Quantity Counter */}
                        <div className="flex items-center border border-border_subtle">
                          <button
                            onClick={() => onUpdateQty(item.productId, item.size, item.color, item.quantity - 1)}
                            data-testid={`cart-qty-decrease-${item.productId}`}
                            className="p-1.5 hover:bg-[#F4F4F2] text-text_secondary hover:text-text_primary transition-colors"
                          >
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className="px-3 text-xs font-mono-accent">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => onUpdateQty(item.productId, item.size, item.color, item.quantity + 1)}
                            data-testid={`cart-qty-increase-${item.productId}`}
                            className="p-1.5 hover:bg-[#F4F4F2] text-text_secondary hover:text-text_primary transition-colors"
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                        {/* Price & Remove */}
                        <div className="flex items-center gap-3">
                          <span className="font-mono-accent font-medium text-sm">
                            {(item.price * item.quantity).toFixed(2)} €
                          </span>
                          <button
                            onClick={() => onRemoveItem(item.productId, item.size, item.color)}
                            data-testid={`cart-item-remove-${item.productId}`}
                            className="text-[#FF3B30] hover:scale-110 transition-transform"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Summary / Total */}
                <div className="pt-6 border-t border-border_subtle space-y-4">
                  <div className="flex justify-between font-mono-accent text-xs uppercase tracking-widest">
                    <span>Subtotal:</span>
                    <span className="font-semibold text-sm">{totalAmount.toFixed(2)} €</span>
                  </div>
                  <div className="flex justify-between font-mono-accent text-[10px] text-text_secondary uppercase tracking-widest">
                    <span>Envío:</span>
                    <span className="text-[#FF3B30]">Gratis (Promocional)</span>
                  </div>
                  <div className="border-t border-dashed border-border_subtle pt-4 flex justify-between font-mono-accent text-xs uppercase tracking-widest font-semibold">
                    <span>Total Estimado:</span>
                    <span className="text-base">{totalAmount.toFixed(2)} €</span>
                  </div>

                  <button
                    onClick={handleStripeCheckout}
                    data-testid="cart-checkout-button"
                    className="w-full py-4 bg-[#0A0A0A] text-white hover:bg-[#FF3B30] transition-colors font-mono-accent uppercase tracking-widest text-xs"
                  >
                    Tramitar Pedido
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
